'use client';
import { Config } from 'constants/config';
import { createRouter, RouteContext } from 'hooks/useRoute';
import React, { ReactNode, useContext, useMemo } from 'react';
import { ClientContext } from '$/modules/context/client';
import { taint } from '$/modules/taint';

export type SerializedPageContext = {
  config: Readonly<Config>;
  route: {
    prefix: string | null;
    locale: string;
    host: string;
    locales: { [prefix: string]: string };
    path: string;
  };
  clientContext: ClientContext;
};

export type PageContext = {
  config: Config;
  route: RouteContext;
};

const PageContext = React.createContext<PageContext | null>(null);
PageContext.displayName = 'PageContext';

/**
 * @deprecated Use `getServerContext()` or `useClientContext()` instead
 */
export function usePageContext(): PageContext {
  const context = useContext(PageContext);
  if (!context) throw new Error('no page context');
  return context;
}

export type PageContextProviderProps = {
  children: ReactNode;
  value?: SerializedPageContext;
};

/**
 * @deprecated Use `<ClientContextProvider>` instead
 */
export function PageContextProvider({
  value,
  children,
}: PageContextProviderProps): JSX.Element {
  const context = useMemo(() => {
    if (value == null) return null;
    const config = taint(
      'Do not pass the entire configuration to the client. It contains secret keys.',
      { ...value.config },
    );
    return {
      config: config,
      route: createRouter(value.config, value.route),
    };
  }, [value]);
  if (!context) return <>{children}</>;
  return (
    <PageContext.Provider value={context}>{children}</PageContext.Provider>
  );
}
